/**
 * PUBLIC CREATIONS PAGE
 * 
 * View another user's nail art creations gallery
 * Route: /creations/:username
 * 
 * Features:
 * - Grid view of user's creations (manis, press-ons, jewelry, etc.)
 * - Category filtering (matches Destash styling)
 * - Lightbox for full-size viewing
 * - Shows marketplace status for items for sale
 * - Link back to user's profile
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { 
  ArrowLeft, 
  Loader2, 
  Palette, 
  X,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Calendar,
  Store,
  Package,
  Filter,
  MessageCircle,
  Heart
} from 'lucide-react';
import PageVisibilityBanner from '../components/PageVisibilityBanner';
import { AppIcons } from '../constants/icons';

interface Creation {
  id: string;
  title: string;
  description?: string;
  photos?: { url: string; isDefault?: boolean }[];
  marketplaceCategory?: 'pressons' | 'jewelry' | 'mani' | 'other';
  creationType?: string;  // mani, pedi, nail-art, practice, press-ons, jewelry, other
  listedOnMarketplace?: boolean;
  price?: number;
  createdAt?: any;
  dateCreated?: string;  // User-entered date (YYYY-MM-DD)
  itemsUsed?: { id: string; name: string; brand: string; itemType: string }[];
  tags?: string[];
}

interface UserProfile {
  username: string;
  displayName?: string;
  avatarUrl?: string;
  privacy?: {
    profileVisibility?: string;
    showCreations?: boolean;
  };
}

export default function PublicCreations() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [creations, setCreations] = useState<Creation[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Read view filter from URL (nailart, marketplace, or all)
  const viewFilter = searchParams.get('filter') || 'all';
  
  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Reset category filter when switching to Creations view (since it's hidden)
  useEffect(() => {
    if (viewFilter === 'nailart') {
      setCategoryFilter('all');
    }
  }, [viewFilter]);
  
  // Lightbox state
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Favorite seller state
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Check if viewing own creations
  const currentUser = getCurrentUser();
  const isOwnCreations = currentUser?.uid === ownerId;

  useEffect(() => {
    const fetchCreations = async () => {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }

      try {
        // Look up user ID from username collection first
        const usernameDoc = await getDoc(doc(db, 'usernames', username.toLowerCase()));
        
        let foundUserId: string | null = null;
        
        if (usernameDoc.exists()) {
          foundUserId = usernameDoc.data()?.userId || usernameDoc.data()?.uid;
        }
        
        // If not found in usernames collection, try querying users
        if (!foundUserId) {
          const usersRef = collection(db, 'users');
          const usersSnapshot = await getDocs(usersRef);
          
          for (const userDoc of usersSnapshot.docs) {
            const userData = userDoc.data();
            if (userData.username?.toLowerCase() === username.toLowerCase() ||
                userData.profile?.username?.toLowerCase() === username.toLowerCase()) {
              foundUserId = userDoc.id;
              break;
            }
          }
        }

        if (!foundUserId) {
          setError('User not found');
          setLoading(false);
          return;
        }

        setOwnerId(foundUserId);

        // Load user profile
        const userDoc = await getDoc(doc(db, 'users', foundUserId));
        if (!userDoc.exists()) {
          setError('Profile not found');
          setLoading(false);
          return;
        }

        const userData = userDoc.data();
        setProfile({
          username: userData.profile?.username || userData.username || username,
          displayName: userData.profile?.displayName || userData.displayName,
          avatarUrl: userData.profile?.avatarUrl || userData.avatarUrl,
          privacy: userData.profile?.privacy || userData.privacy
        });

        // Check if creations are public (default to true if not set)
        const showCreations = userData.profile?.privacy?.showCreations !== false && 
                             userData.privacy?.showCreations !== false;
        
        const isOwn = currentUser?.uid === foundUserId;
        if (!showCreations && !isOwn) {
          setError('This user\'s creations are private');
          setLoading(false);
          return;
        }

        // Fetch creations (without orderBy to avoid index requirement)
        const creationsRef = collection(db, 'users', foundUserId, 'creations');
        const creationsSnapshot = await getDocs(creationsRef);
        
        const userCreations: Creation[] = creationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Creation));
        
        // Sort by dateCreated (user-entered) first, fallback to createdAt (when added)
        userCreations.sort((a, b) => {
          // Prefer dateCreated (string like "2025-01-10"), fallback to createdAt
          const dateA = a.dateCreated 
            ? new Date(a.dateCreated) 
            : (a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0));
          const dateB = b.dateCreated 
            ? new Date(b.dateCreated) 
            : (b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0));
          return dateB.getTime() - dateA.getTime();
        });
        
        setCreations(userCreations);
      } catch (err) {
        console.error('Error fetching creations:', err);
        setError('Failed to load creations');
      } finally {
        setLoading(false);
      }
    };

    fetchCreations();
  }, [username, currentUser?.uid]);

  // Check if user has favorited this seller
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!currentUser?.uid || !ownerId || currentUser.uid === ownerId) return;
      
      try {
        const favoriteDoc = await getDoc(
          doc(db, 'users', currentUser.uid, 'favoriteSellers', ownerId)
        );
        setIsFavorited(favoriteDoc.exists());
      } catch (err) {
        console.error('Error checking favorite status:', err);
      }
    };
    
    checkFavoriteStatus();
  }, [currentUser?.uid, ownerId]);

  // Toggle favorite seller
  const toggleFavorite = async () => {
    if (!currentUser?.uid || !ownerId || !profile) return;
    
    setFavoriteLoading(true);
    try {
      const favoriteRef = doc(db, 'users', currentUser.uid, 'favoriteSellers', ownerId);
      
      if (isFavorited) {
        // Remove from favorites
        await deleteDoc(favoriteRef);
        setIsFavorited(false);
      } else {
        // Add to favorites
        await setDoc(favoriteRef, {
          sellerId: ownerId,
          sellerUsername: profile.username,
          sellerAvatar: profile.avatarUrl || null,
          addedAt: serverTimestamp()
        });
        setIsFavorited(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Helper to determine display type (must be before filteredCreations)
  const getDisplayType = (creation: Creation): string => {
    // If creationType is set, not empty, and not 'other', use it
    if (creation.creationType && creation.creationType.trim() !== '' && creation.creationType !== 'other') {
      return creation.creationType;
    }
    // If it's 'other' but explicitly a marketplace item, keep it as 'other'
    if (creation.creationType === 'other' && creation.listedOnMarketplace) {
      return 'other';
    }
    // Fallback for old data using marketplaceCategory
    if (creation.marketplaceCategory === 'pressons') return 'press-ons';
    if (creation.marketplaceCategory === 'jewelry') return 'jewelry';
    // Default to 'mani' for everything else
    return 'mani';
  };

  // Filter creations by view filter (nailart/marketplace) and category
  const filteredCreations = creations.filter(creation => {
    // First apply view filter (nailart vs marketplace)
    if (viewFilter === 'nailart' && creation.listedOnMarketplace) return false;
    if (viewFilter === 'marketplace' && !creation.listedOnMarketplace) return false;
    
    // Then apply category filter using display type
    if (categoryFilter === 'all') return true;
    const displayType = getDisplayType(creation);
    if (categoryFilter === 'mani') {
      return displayType === 'mani' || displayType === 'pedi' || displayType === 'nail-art' || displayType === 'practice';
    }
    if (categoryFilter === 'pressons') return displayType === 'press-ons';
    if (categoryFilter === 'jewelry') return displayType === 'jewelry';
    if (categoryFilter === 'other') return displayType === 'other';
    return true;
  });

  // Lightbox navigation
  const goToPrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedIndex !== null && selectedIndex < filteredCreations.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      
      if (e.key === 'Escape') setSelectedIndex(null);
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, filteredCreations.length]);

  // Get creation image
  const getCreationImage = (creation: Creation) => {
    if (!creation.photos?.length) return null;
    const defaultPhoto = creation.photos.find(p => p.isDefault);
    return defaultPhoto?.url || creation.photos[0]?.url;
  };

  // Get category label and icon based on display type
  const getCategoryInfo = (displayType: string) => {
    switch (displayType) {
      case 'mani': return { label: 'Manicure', Icon: AppIcons.Creations };
      case 'pedi': return { label: 'Pedicure', Icon: AppIcons.Creations };
      case 'nail-art': return { label: 'Nail Art', Icon: AppIcons.Creations };
      case 'practice': return { label: 'Practice', Icon: AppIcons.Creations };
      case 'press-ons': return { label: 'Press-Ons', Icon: AppIcons.PressOns };
      case 'jewelry': return { label: 'Jewelry', Icon: AppIcons.Jewelry };
      case 'other': return { label: 'Other', Icon: AppIcons.Creations };
      default: return { label: 'Manicure', Icon: AppIcons.Creations };
    }
  };

  // Get badge color based on display type
  const getTypeBadgeColor = (displayType: string) => {
    switch (displayType) {
      case 'mani': return 'bg-pink-500';
      case 'pedi': return 'bg-rose-500';
      case 'nail-art': return 'bg-violet-500';
      case 'practice': return 'bg-slate-500';
      case 'press-ons': return 'bg-purple-500';
      case 'jewelry': return 'bg-amber-500';
      case 'other': return 'bg-teal-500';
      default: return 'bg-pink-500';
    }
  };

  // Get base creations based on view filter
  const baseCreations = creations.filter(c => {
    if (viewFilter === 'nailart') return !c.listedOnMarketplace;
    if (viewFilter === 'marketplace') return c.listedOnMarketplace;
    return true;
  });

  // Get category counts (based on view-filtered creations, using display type)
  const categoryCounts = {
    all: baseCreations.length,
    mani: baseCreations.filter(c => {
      const type = getDisplayType(c);
      return type === 'mani' || type === 'pedi' || type === 'nail-art' || type === 'practice';
    }).length,
    pressons: baseCreations.filter(c => getDisplayType(c) === 'press-ons').length,
    jewelry: baseCreations.filter(c => getDisplayType(c) === 'jewelry').length,
    other: baseCreations.filter(c => getDisplayType(c) === 'other').length,
  };

  // Format date
  const formatDate = (timestamp?: any) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return '';
    }
  };

  const selectedCreation = selectedIndex !== null ? filteredCreations[selectedIndex] : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-glass" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Palette className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-glass mb-2 font-heading">
            {error}
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="text-glass hover:text-glass-secondary"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Visibility Banner - Only for profile owner */}
        {isOwnCreations && profile?.username && (
          <PageVisibilityBanner 
            isPublic={profile?.privacy?.showCreations !== false}
            publicPath={`/creations/${profile.username}`}
            pageType="creations"
            className="mb-6"
            isPreviewMode={true}
            onPreview={() => navigate('/creations')}
          />
        )}

        {/* Header - Matching DestashPage style */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-800/60 rounded-lg transition-default"
          >
            <ArrowLeft className="icon-base text-glass-secondary" />
          </button>
          
          <div className="flex items-center gap-4 flex-1">
            {profile?.avatarUrl ? (
              <img 
                src={profile.avatarUrl} 
                alt={profile.displayName || profile.username}
                className="w-14 h-14 rounded-full object-cover border-2 border-slate-600"
              />
            ) : (
              <div className="w-14 h-14 rounded-full rainbow-gradient flex items-center justify-center text-white text-xl font-bold">
                {(profile?.displayName || profile?.username || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-glass font-heading">
                {profile?.displayName || profile?.username}'s Creations
              </h1>
              <p className="text-glass-secondary font-body">
                {creations.length} creation{creations.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* View Profile Link */}
          <Link
            to={`/profile/${username}`}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 hover:bg-white/70 rounded-lg text-glass-secondary transition-default font-body"
          >
            <ExternalLink className="icon-sm" />
            View Profile
          </Link>
          
          {/* Favorite Seller Button - Only show for other users' pages */}
          {currentUser && !isOwnCreations && ownerId && (
            <button
              onClick={toggleFavorite}
              disabled={favoriteLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all font-body ${
                isFavorited
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg'
                  : 'bg-slate-800/60 hover:bg-white/70 text-glass-secondary border border-slate-600'
              }`}
              title={isFavorited ? 'Unfollow this creator' : 'Follow this creator'}
            >
              {favoriteLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
              )}
              <span className="hidden sm:inline">
                {isFavorited ? 'Following' : 'Follow'}
              </span>
            </button>
          )}
        </div>

        {/* View Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <Link
            to={`/creations/${username}`}
            className={`px-4 py-2 rounded-lg font-medium transition-default font-body ${
              viewFilter === 'all'
                ? 'bg-slate-700 text-white'
                : 'bg-slate-800/60 text-glass-secondary hover:bg-white/70'
            }`}
          >
            All ({creations.length})
          </Link>
          <Link
            to={`/creations/${username}?filter=nailart`}
            className={`px-4 py-2 rounded-lg font-medium transition-default font-body flex items-center gap-2 ${
              viewFilter === 'nailart'
                ? 'bg-pink-500 text-white'
                : 'bg-slate-800/60 text-glass-secondary hover:bg-white/70'
            }`}
          >
            💅 Creations ({creations.filter(c => !c.listedOnMarketplace).length})
          </Link>
          <Link
            to={`/creations/${username}?filter=marketplace`}
            className={`px-4 py-2 rounded-lg font-medium transition-default font-body flex items-center gap-2 ${
              viewFilter === 'marketplace'
                ? 'bg-green-500 text-white'
                : 'bg-slate-800/60 text-glass-secondary hover:bg-white/70'
            }`}
          >
            🛒 For Sale ({creations.filter(c => c.listedOnMarketplace).length})
          </Link>
        </div>

        {creations.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Palette className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-glass mb-2 font-body">
              No creations yet
            </h2>
            <p className="text-glass-secondary font-body">
              {isOwnCreations 
                ? "You haven't uploaded any nail art yet."
                : "This user hasn't shared any nail art yet."}
            </p>
            {isOwnCreations && (
              <Link
                to="/creations"
                className="inline-block mt-4 px-6 py-2 btn-primary rounded-lg transition-default"
              >
                Go to My Creations
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Category Filter Bar - Only show for All or For Sale views, not Creations */}
            {viewFilter !== 'nailart' && (
              <div className="glass-card p-4 mb-6">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 hover:bg-white/80 rounded-lg text-glass-secondary transition-default md:hidden"
                  >
                    <Filter className="icon-sm" />
                    Filter
                  </button>
                  
                  <div className={`flex flex-wrap gap-2 ${showFilters ? 'block' : 'hidden md:flex'}`}>
                    <button
                      onClick={() => setCategoryFilter('all')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-default font-body ${
                        categoryFilter === 'all'
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                      }`}
                    >
                      All ({categoryCounts.all})
                    </button>
                    {categoryCounts.mani > 0 && (
                      <button
                        onClick={() => setCategoryFilter('mani')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-default font-body flex items-center gap-1.5 ${
                          categoryFilter === 'mani'
                            ? 'bg-pink-500 text-white'
                            : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                        }`}
                      >
                        <AppIcons.Creations className="icon-xs" /> Manicures ({categoryCounts.mani})
                      </button>
                    )}
                    {categoryCounts.pressons > 0 && (
                      <button
                        onClick={() => setCategoryFilter('pressons')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-default font-body flex items-center gap-1.5 ${
                          categoryFilter === 'pressons'
                            ? 'bg-purple-500 text-white'
                            : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                        }`}
                      >
                        <AppIcons.PressOns className="icon-xs" /> Press-Ons ({categoryCounts.pressons})
                      </button>
                    )}
                    {categoryCounts.jewelry > 0 && (
                      <button
                        onClick={() => setCategoryFilter('jewelry')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-default font-body flex items-center gap-1.5 ${
                          categoryFilter === 'jewelry'
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                        }`}
                      >
                        <AppIcons.Jewelry className="icon-xs" /> Jewelry ({categoryCounts.jewelry})
                      </button>
                    )}
                    {categoryCounts.other > 0 && (
                      <button
                        onClick={() => setCategoryFilter('other')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-default font-body flex items-center gap-1.5 ${
                          categoryFilter === 'other'
                            ? 'bg-teal-500 text-white'
                            : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                        }`}
                      >
                        <AppIcons.Creations className="icon-xs" /> Other ({categoryCounts.other})
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Creations Grid */}
            {filteredCreations.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredCreations.map((creation, index) => (
                <div
                  key={creation.id}
                  onClick={() => setSelectedIndex(index)}
                  className="glass-card-sm overflow-hidden group cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all"
                >
                  {/* Image */}
                  <div className="aspect-square relative overflow-hidden">
                    {getCreationImage(creation) ? (
                      <img
                        src={getCreationImage(creation)!}
                        alt={creation.title || 'Nail art creation'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-fuchsia-100 to-purple-100 flex items-center justify-center">
                        <Palette className="icon-2xl text-glass-secondary" />
                      </div>
                    )}
                    
                    {/* Category Badge */}
                    {(() => {
                      const displayType = getDisplayType(creation);
                      const categoryInfo = getCategoryInfo(displayType);
                      const IconComponent = categoryInfo.Icon;
                      return (
                        <span className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1 ${getTypeBadgeColor(displayType)}`}>
                          <IconComponent className="icon-xs" /> {categoryInfo.label}
                        </span>
                      );
                    })()}

                    {/* For Sale Badge */}
                    {creation.listedOnMarketplace && (
                      <span className="absolute top-2 right-2 p-1.5 rounded-full bg-green-500 text-white" title="For Sale">
                        <Store className="icon-xs" />
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="font-bold text-glass truncate font-body">
                      {creation.title}
                    </p>
                    {creation.listedOnMarketplace && creation.price && (
                      <p className="text-green-600 text-sm font-bold">
                        ${creation.price.toFixed(2)}
                      </p>
                    )}
                    {!creation.listedOnMarketplace && (creation.dateCreated || creation.createdAt) && (
                      <p className="text-xs text-glass-muted">
                        {creation.dateCreated 
                          ? new Date(creation.dateCreated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : formatDate(creation.createdAt)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              </div>
            ) : (
              <div className="glass-card p-12 text-center">
                <Palette className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-glass mb-2 font-body">
                  No {viewFilter === 'marketplace' ? 'items for sale' : viewFilter === 'nailart' ? 'nail art creations' : 'creations'} found
                </h2>
                <p className="text-glass-secondary font-body">
                  {viewFilter === 'marketplace' 
                    ? "This user doesn't have any items listed for sale."
                    : viewFilter === 'nailart'
                    ? "This user hasn't shared any nail art creations yet."
                    : "Try adjusting your filters."}
                </p>
                {viewFilter !== 'all' && (
                  <Link
                    to={`/creations/${username}`}
                    className="inline-block mt-4 px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-default"
                  >
                    View All Creations
                  </Link>
                )}
              </div>
            )}
          </>
        )}

        {/* Lightbox */}
        {selectedCreation && (
          <div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setSelectedIndex(null)}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-default z-10"
            >
              <X className="icon-lg" />
            </button>

            {/* Navigation arrows */}
            {selectedIndex !== null && selectedIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white transition-default"
              >
                <ChevronLeft className="icon-xl" />
              </button>
            )}
            {selectedIndex !== null && selectedIndex < filteredCreations.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white transition-default"
              >
                <ChevronRight className="icon-xl" />
              </button>
            )}

            {/* Image and details */}
            <div 
              className="max-w-5xl w-full mx-4 flex flex-col md:flex-row gap-6 max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image */}
              <div className="flex-1 flex items-center justify-center">
                {getCreationImage(selectedCreation) ? (
                  <img
                    src={getCreationImage(selectedCreation)!}
                    alt={selectedCreation.title || 'Nail art creation'}
                    className="max-h-[80vh] max-w-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="w-96 h-96 bg-gradient-to-br from-fuchsia-100 to-purple-100 rounded-lg flex items-center justify-center">
                    <Palette className="w-24 h-24 text-glass-secondary" />
                  </div>
                )}
              </div>

              {/* Details Panel */}
              <div className="md:w-80 glass-modal p-6 overflow-y-auto max-h-[80vh]">
                {/* Category Badge */}
                {(() => {
                  const displayType = getDisplayType(selectedCreation);
                  const categoryInfo = getCategoryInfo(displayType);
                  const IconComponent = categoryInfo.Icon;
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white mb-3 ${getTypeBadgeColor(displayType)}`}>
                      <IconComponent className="icon-xs" /> {categoryInfo.label}
                    </span>
                  );
                })()}

                {/* Title */}
                <h2 className="text-xl font-bold text-glass mb-2 font-body">
                  {selectedCreation.title}
                </h2>

                {/* Date */}
                {(selectedCreation.dateCreated || selectedCreation.createdAt) && (
                  <p className="text-sm text-glass-muted flex items-center gap-1 mb-4">
                    <Calendar className="icon-sm" />
                    {selectedCreation.dateCreated 
                      ? new Date(selectedCreation.dateCreated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : formatDate(selectedCreation.createdAt)}
                  </p>
                )}

                {/* For Sale Badge */}
                {selectedCreation.listedOnMarketplace && (
                  <div className="mb-4 p-3 bg-green-500/20 rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-2 text-glass">
                      <Store className="icon-md" />
                      <span className="font-bold">For Sale</span>
                    </div>
                    {selectedCreation.price && (
                      <p className="text-2xl font-bold text-green-400 mt-1">
                        ${selectedCreation.price.toFixed(2)}
                      </p>
                    )}
                    {!isOwnCreations && currentUser && (
                      <button
                        onClick={() => {
                          navigate(`/messages?to=${ownerId}&about=creation&id=${selectedCreation.id}`);
                        }}
                        className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600/80 hover:bg-green-600 text-white rounded-lg font-medium transition-default"
                      >
                        <MessageCircle className="icon-sm" />
                        Contact Seller
                      </button>
                    )}
                  </div>
                )}

                {/* Description */}
                {selectedCreation.description && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-glass-secondary mb-1">Description</p>
                    <p className="text-glass-secondary font-body">
                      {selectedCreation.description}
                    </p>
                  </div>
                )}

                {/* Tags */}
                {selectedCreation.tags && selectedCreation.tags.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-glass-secondary mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCreation.tags.map((tag, i) => (
                        <span 
                          key={i}
                          className="px-2 py-1 bg-slate-700 text-glass-secondary rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Items Used */}
                {selectedCreation.itemsUsed && selectedCreation.itemsUsed.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-glass-secondary mb-2 flex items-center gap-1">
                      <Package className="icon-sm" /> Items Used
                    </p>
                    <div className="space-y-2">
                      {selectedCreation.itemsUsed.map((item, i) => (
                        <div key={i} className="text-sm">
                          <span className="text-glass font-medium">{item.brand}</span>
                          <span className="text-glass-secondary"> - {item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Counter */}
                <div className="text-center text-sm text-glass-secondary pt-4 border-t border-slate-600">
                  {(selectedIndex ?? 0) + 1} of {filteredCreations.length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
